import { useEffect, useState } from 'react';
import { Tabs, Tab, Form, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Table from '../../../core/common/dataTable/index';
import { RootState } from '../../../core/data/redux/store';
import {
    setDistricts,
    setBlocks,
    setVillages,
    setLoading,
    setError,
    deleteDistrict,
    deleteBlock,
    deleteVillages,
} from '../../../core/data/redux/stateDistrictSlice';
import {
    fetchDistricts,
    fetchBlocks,
    fetchVillages,
    deleteDistrict as deleteDistrictService,
    deleteBlock as deleteBlockService,
    deleteVillage as deleteVillageService,
} from '../../../core/data/services/stateDistrictBlockService';
import { District, Block, Village } from '../../../core/data/types/stateDistrict';
import DistrictForm from './districtForm';
import BlockForm from './blockForm';
import VillageForm from './villageForm';
import { getUserDetails } from '../../../lib/getUserDetails';
import { fetchAdminApi } from '../../../core/data/services/adminService';

const StateAdmin = () => {
    const dispatch = useDispatch();
    const { districts, blocks, villages, loading } = useSelector((state: RootState) => state.stateDistrict);
    const stateAdminUser = getUserDetails();
    const stateId = stateAdminUser[0]?.relatedEntity?.states?.id

    console.log("state Admin ", stateId)
    console.log("state Admin user ", stateAdminUser)





    const userStateId = stateId;  //fetch from auth context or Redux

    // State management
    const [selectedState] = useState<number>(userStateId); // Lock to user's stateId
    const [selectedDistrict, setSelectedDistrict] = useState<number | undefined>(undefined);
    const [selectedBlock, setSelectedBlock] = useState<number | undefined>(undefined);
    const [selectedVillage, setSelectedVillage] = useState<number | undefined>(undefined);
    const [activeTab, setActiveTab] = useState<string>('districts'); // Default to districts tab

    // Modal states
    const [showDistrictForm, setShowDistrictForm] = useState(false);
    const [showBlockForm, setShowBlockForm] = useState(false);
    const [showVillageForm, setShowVillageForm] = useState(false);
    const [editDistrict, setEditDistrict] = useState<District | undefined>(undefined);
    const [editBlock, setEditBlock] = useState<Block | undefined>(undefined);
    const [editVillage, setEditVillage] = useState<Village | undefined>(undefined);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteItem, setDeleteItem] = useState<{ id: number; type: 'district' | 'block' | 'village' } | null>(null);

    // Fetch districts for the user's state on mount
    useEffect(() => {
        const loadDistricts = async () => {
            dispatch(setLoading(true));
            try {
                const response = await fetchDistricts(selectedState);
                dispatch(setDistricts(response));
            } catch (err: any) {
                dispatch(setError(err.message || 'Failed to fetch districts'));
                dispatch(setDistricts([]));
            }
        };
        loadDistricts();
    }, [selectedState, dispatch]);

    // Fetch blocks when a district is selected
    useEffect(() => {
        const loadBlocks = async () => {
            if (selectedDistrict) {
                dispatch(setLoading(true));
                try {
                    const response = await fetchBlocks(selectedState, selectedDistrict);
                    dispatch(setBlocks(response));
                } catch (err: any) {
                    dispatch(setError(err.message || 'Failed to fetch blocks'));
                    dispatch(setBlocks([]));
                }
            } else {
                dispatch(setBlocks([]));
                dispatch(setVillages([]));
                setSelectedBlock(undefined);
                setSelectedVillage(undefined);
            }
        };
        loadBlocks();
    }, [selectedState, selectedDistrict, dispatch]);

    // Fetch villages when a block is selected
    useEffect(() => {
        const loadVillages = async () => {
            if (selectedBlock) {
                dispatch(setLoading(true));
                try {
                    const response = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
                    dispatch(setVillages(response));
                } catch (err: any) {
                    dispatch(setError(err.message || 'Failed to fetch villages'));
                    dispatch(setVillages([]));
                }
            } else {
                dispatch(setVillages([]));
                setSelectedVillage(undefined);
            }
        };
        loadVillages();
    }, [selectedState, selectedDistrict, selectedBlock, dispatch]);

    // Table columns with action buttons
    const districtColumns = [
        { title: 'ID', dataIndex: 'id', sorter: (a: District, b: District) => a.id - b.id },
        { title: 'Name', dataIndex: 'name', sorter: (a: District, b: District) => a.name.localeCompare(b.name) },
        { title: 'State ID', dataIndex: 'stateId', sorter: (a: District, b: District) => a.stateId - b.stateId },
        {
            title: 'Actions',
            render: (record: District) => (
                <>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                            setEditDistrict(record);
                            setShowDistrictForm(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                            setDeleteItem({ id: record.id, type: 'district' });
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>
                </>
            ),
        },
    ];

    const blockColumns = [
        { title: 'ID', dataIndex: 'id', sorter: (a: Block, b: Block) => a.id - b.id },
        { title: 'Name', dataIndex: 'name', sorter: (a: Block, b: Block) => a.name.localeCompare(b.name) },
        { title: 'District ID', dataIndex: 'districtId', sorter: (a: Block, b: Block) => a.districtId - b.districtId },
        {
            title: 'Actions',
            render: (record: Block) => (
                <>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                            setEditBlock(record);
                            setShowBlockForm(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                            setDeleteItem({ id: record.id, type: 'block' });
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>
                </>
            ),
        },
    ];

    const villageColumns = [
        { title: 'ID', dataIndex: 'id', sorter: (a: Village, b: Village) => a.id - b.id },
        { title: 'Name', dataIndex: 'name', sorter: (a: Village, b: Village) => a.name.localeCompare(b.name) },
        { title: 'Block ID', dataIndex: 'blockAreaId', sorter: (a: Village, b: Village) => a.blockAreaId - b.blockAreaId },
        {
            title: 'Actions',
            render: (record: Village) => (
                <>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                            setEditVillage(record);
                            setShowVillageForm(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                            setDeleteItem({ id: record.id, type: 'village' });
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>
                </>
            ),
        },
    ];

    // Filter data based on selections and active tab
    const filteredDistricts = Array.isArray(districts)
        ? selectedDistrict && activeTab === 'districts'
            ? districts.filter((district) => district.id === selectedDistrict && district.stateId === selectedState)
            : districts.filter((district) => district.stateId === selectedState)
        : [];
    const filteredBlocks = Array.isArray(blocks)
        ? selectedBlock && activeTab === 'blocks'
            ? blocks.filter((block) => block.id === selectedBlock && block.districtId === selectedDistrict)
            : selectedDistrict
                ? blocks.filter((block) => block.districtId === selectedDistrict)
                : blocks
        : [];
    const filteredVillages = Array.isArray(villages)
        ? selectedVillage && activeTab === 'villages'
            ? villages.filter((village) => village.id === selectedVillage && village.blockAreaId === selectedBlock)
            : selectedBlock
                ? villages.filter((village) => village.blockAreaId === selectedBlock)
                : villages
        : [];

    const handleDelete = async () => {
        if (!deleteItem) return;
        try {
            dispatch(setLoading(true));
            if (deleteItem.type === 'district') {
                await deleteDistrictService(deleteItem.id);
                dispatch(deleteDistrict(deleteItem.id));
                setSelectedDistrict(undefined);
                setSelectedBlock(undefined);
                setSelectedVillage(undefined);
                const districts = await fetchDistricts(selectedState);
                dispatch(setDistricts(districts));
            } else if (deleteItem.type === 'block') {
                await deleteBlockService(deleteItem.id);
                dispatch(deleteBlock(deleteItem.id));
                setSelectedBlock(undefined);
                setSelectedVillage(undefined);
                if (selectedDistrict) {
                    const blocks = await fetchBlocks(selectedState, selectedDistrict);
                    dispatch(setBlocks(blocks));
                }
            } else if (deleteItem.type === 'village') {
                await deleteVillageService(deleteItem.id);
                dispatch(deleteVillages(deleteItem.id));
                setSelectedVillage(undefined);
                if (selectedBlock) {
                    const villages = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
                    dispatch(setVillages(villages));
                }
            }
            setShowDeleteModal(false);
            setDeleteItem(null);
        } catch (err: any) {
            dispatch(setError(err.message || 'Failed to delete'));
        } finally {
            dispatch(setLoading(false));
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="my-auto mb-2">
                        <h3 className="page-title">State Admin Management</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    State Name
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {stateAdminUser[0]?.relatedEntity?.states?.name}
                                </li>
                            </ol>
                        </nav>
                    </div>

                    <Button
                        variant="primary"
                        onClick={() => {
                            if (activeTab === 'districts') {
                                setEditDistrict(undefined);
                                setShowDistrictForm(true);
                            } else if (activeTab === 'blocks' && selectedDistrict) {
                                setEditBlock(undefined);
                                setShowBlockForm(true);
                            } else if (activeTab === 'villages' && selectedBlock) {
                                setEditVillage(undefined);
                                setShowVillageForm(true);
                            }
                        }}
                        disabled={
                            (activeTab === 'blocks' && !selectedDistrict) ||
                            (activeTab === 'villages' && !selectedBlock)
                        }
                    >
                        {activeTab === 'districts' ? 'Add District' : activeTab === 'blocks' ? 'Add Block' : 'Add Village'}
                    </Button>
                </div>

                {/* Dropdowns and Tabs */}
                <div className="row mb-3 align-items-end">
                    <div className="col-md-3">
                        <Form.Group>
                            <Form.Label>Select District</Form.Label>
                            <Form.Select
                                value={selectedDistrict || ''}
                                onChange={(e) => {
                                    setSelectedDistrict(e.target.value ? Number(e.target.value) : undefined);
                                    setSelectedBlock(undefined);
                                    setSelectedVillage(undefined);
                                    setActiveTab('districts');
                                }}
                            >
                                <option value="">Select a district</option>
                                {Array.isArray(districts) &&
                                    districts
                                        .filter((district) => district.stateId === selectedState)
                                        .map((district) => (
                                            <option key={district.id} value={district.id}>
                                                {district.name}
                                            </option>
                                        ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <div className="col-md-3">
                        <Form.Group>
                            <Form.Label>Select Block</Form.Label>
                            <Form.Select
                                value={selectedBlock || ''}
                                onChange={(e) => {
                                    setSelectedBlock(e.target.value ? Number(e.target.value) : undefined);
                                    setSelectedVillage(undefined);
                                    setActiveTab('blocks');
                                }}
                                disabled={!selectedDistrict}
                            >
                                <option value="">Select a block</option>
                                {Array.isArray(blocks) &&
                                    blocks
                                        .filter((block) => block.districtId === selectedDistrict)
                                        .map((block) => (
                                            <option key={block.id} value={block.id}>
                                                {block.name}
                                            </option>
                                        ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <div className="col-md-3">
                        <Form.Group>
                            <Form.Label>Select Village</Form.Label>
                            <Form.Select
                                value={selectedVillage || ''}
                                onChange={(e) => {
                                    setSelectedVillage(e.target.value ? Number(e.target.value) : undefined);
                                    setActiveTab('villages');
                                }}
                                disabled={!selectedBlock}
                            >
                                <option value="">Select a village</option>
                                {Array.isArray(villages) &&
                                    villages
                                        .filter((village) => village.blockAreaId === selectedBlock)
                                        .map((village) => (
                                            <option key={village.id} value={village.id}>
                                                {village.name}
                                            </option>
                                        ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <div className="col-md-4 d-flex mt-3">
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(key) => setActiveTab(key || 'districts')}
                            id="location-tabs"
                            className="mb-0"
                        >
                            <Tab eventKey="districts" title="Districts" />
                            <Tab eventKey="blocks" title="Blocks" disabled={!selectedDistrict} />
                            <Tab eventKey="villages" title="Villages" disabled={!selectedBlock} />
                        </Tabs>
                    </div>
                </div>

                {/* Table */}
                <div className="card">
                    <div className="card-body p-0 py-3">
                        {loading ? (
                            <div className="text-center py-5">
                                <i className="bi bi-arrow-repeat animate-spin" style={{ fontSize: '3rem' }}></i>
                                <h5 className="mt-3">Loading...</h5>
                            </div>
                        ) : activeTab === 'districts' && filteredDistricts.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="mt-3 text-muted">No Districts Found</h5>
                            </div>
                        ) : activeTab === 'blocks' && filteredBlocks.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="mt-3 text-muted">No Blocks Found</h5>
                            </div>
                        ) : activeTab === 'villages' && filteredVillages.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="mt-3 text-muted">No Villages Found</h5>
                            </div>
                        ) : (
                            <Table
                                dataSource={
                                    activeTab === 'districts'
                                        ? filteredDistricts
                                        : activeTab === 'blocks'
                                            ? filteredBlocks
                                            : filteredVillages
                                }
                                columns={
                                    activeTab === 'districts'
                                        ? districtColumns
                                        : activeTab === 'blocks'
                                            ? blockColumns
                                            : villageColumns
                                }
                                Selection={false}
                                pagination={true}
                            />
                        )}
                    </div>
                </div>

                {/* Modals */}
                <DistrictForm
                    show={showDistrictForm}
                    onHide={() => setShowDistrictForm(false)}
                    district={editDistrict}
                    stateId={selectedState}
                />
                {selectedDistrict && (
                    <BlockForm
                        show={showBlockForm}
                        onHide={() => setShowBlockForm(false)}
                        block={editBlock}
                        stateId={selectedState}
                        districtId={selectedDistrict}
                        onSuccess={() => setActiveTab('blocks')}

                    />
                )}
                {selectedBlock && (
                    <VillageForm
                        show={showVillageForm}
                        onHide={() => setShowVillageForm(false)}
                        village={editVillage}
                        stateId={selectedState}
                        districtId={selectedDistrict!}
                        blockAreaId={selectedBlock}
                    />
                )}

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this {deleteItem?.type}?
                    </Modal.Body>
                    <Modal.Footer className='gap-2'>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDelete}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default StateAdmin;


